import { mapResourceType } from '@/constants/resources/map-resource-type';
import {
  type EditPathInput,
  ResourceTypes,
} from '@/data/learning-path/interfaces';
import { ResourceTypes as ResourceTypesEnum } from '@/models/resources';

const DEFAULT_COVER_IMAGE_CROP_AREA = { x: 0, y: 0, width: 320, height: 128 };

const normalizeCategory = (category: string): string => {
  const englishValues = Object.values(ResourceTypesEnum) as string[];
  if (englishValues.includes(category)) {
    return category;
  }

  const mapped = mapResourceType[category as keyof typeof mapResourceType];
  return mapped || category;
};

export const editFormData = (data: EditPathInput): FormData => {
  const formData = new FormData();
  const { contents } = data.learning_path;

  formData.append('path[id]', data.id);
  formData.append('path[title]', data.title);
  formData.append('path[description]', data.description);

  const headerImage = data.header_image;
  const hasImage = headerImage?.blob || headerImage?.previewUrl;
  const hasImageChanged = data.has_image_changed ?? false;

  if (headerImage && hasImage) {
    const {
      blob,
      cropArea: { width, height, x, y },
    } = headerImage;

    if (blob) {
      formData.append('path[header_image][blob]', blob);
    }

    if (hasImageChanged) {
      formData.append('path[header_image][width]', String(width));
      formData.append('path[header_image][height]', String(height));
      formData.append('path[header_image][x]', String(x));
      formData.append('path[header_image][y]', String(y));
    }
  }

  contents.forEach((block) => {
    formData.append(`path[contents][][position]`, String(block.position));
    formData.append(`path[contents][][type]`, block.type);

    if (block.type === ResourceTypes.Text) {
      formData.append(`path[contents][][content][body]`, block.content.body);
    }

    if (block.type === ResourceTypes.ResourceGroup) {
      block.content.resource_groups.forEach((resource, index) => {
        const base = `path[contents][][content][resource_groups][${index}]`;
        const externalLink = `${base}[external_link]`;
        const course = `${base}[course]`;

        if ('external_link' in resource) {
          const category = resource.external_link?.category;
          const coverImage = resource.external_link?.cover_image;

          formData.append(
            `${externalLink}[name]`,
            resource.external_link?.name || ''
          );
          if (category) {
            formData.append(
              `${externalLink}[category]`,
              normalizeCategory(category)
            );
          }
          formData.append(
            `${externalLink}[url]`,
            resource.external_link?.url || ''
          );

          if (coverImage?.blob) {
            const { x, y, width, height } =
              coverImage.cropArea ?? DEFAULT_COVER_IMAGE_CROP_AREA;

            formData.append(
              `${externalLink}[cover_image][blob]`,
              coverImage.blob
            );
            formData.append(`${externalLink}[cover_image][x]`, String(x));
            formData.append(`${externalLink}[cover_image][y]`, String(y));
            formData.append(
              `${externalLink}[cover_image][width]`,
              String(width)
            );
            formData.append(
              `${externalLink}[cover_image][height]`,
              String(height)
            );
          }
        }

        if ('course' in resource) {
          formData.append(`${course}[name]`, resource.course?.name || '');
          formData.append(
            `${course}[institution_name]`,
            resource.course?.institution_name || ''
          );
          formData.append(
            `${course}[course_type]`,
            resource.course?.course_type || ''
          );
          formData.append(
            `${course}[modality]`,
            resource.course?.modality || ''
          );
          formData.append(
            `${course}[external_course_id]`,
            resource.course?.external_course_id || ''
          );
          formData.append(
            `${course}[external_institution_background_color]`,
            resource.course?.external_institution_background_color || ''
          );
          formData.append(
            `${course}[external_institution_logo_url]`,
            resource.course?.external_institution_logo_url || ''
          );
          formData.append(
            `${course}[external_institution_id]`,
            String(resource.course?.institution_id || 0)
          );
        }
      });
    }
  });

  return formData;
};
